import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CreditCard, Lock, LifeBuoy, LogOut, Loader2 } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const BRAND_BLUE = '#2B4B6A';

/**
 * Where a 402 lands.
 *
 * Deliberately outside the Sidebar shell: the shell's own data calls are
 * paywalled, so rendering it here would fill the screen with failed requests.
 * This page only calls endpoints the middleware always allows.
 */
const SubscriptionRequired = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/billing-status');
        if (cancelled) return;
        setStatus(res.data);
        // If billing was fixed in another tab (or enforcement was switched
        // off), don't strand the user on a dead end.
        if (res.data?.allowed) navigate('/dashboard', { replace: true });
      } catch (err) {
        if (!cancelled) setStatus(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const isCompanyAdmin = user?.role === 'company_admin';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
        <div className="w-14 h-14 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: '#EEF2F6' }}>
          <Lock className="w-7 h-7" style={{ color: BRAND_BLUE }} />
        </div>

        <h1 className="text-xl font-bold text-gray-900">Your subscription is not active</h1>
        <p className="text-sm text-gray-500 mt-3 leading-relaxed">
          {status?.message
            || 'An active subscription is required to continue using Trados. Your data is safe and nothing has been deleted — access resumes the moment a plan is active.'}
        </p>

        <div className="mt-7 space-y-3">
          {isCompanyAdmin ? (
            <Link
              to="/admin/billing"
              data-testid="subscription-required-billing"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold text-white"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              <CreditCard className="w-4 h-4" />
              Choose a plan
            </Link>
          ) : (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-4">
              Ask your company administrator to activate a subscription. They can do it
              from <span className="font-medium">Billing &amp; Subscription</span>.
            </p>
          )}

          <Link
            to="/support"
            data-testid="subscription-required-support"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            <LifeBuoy className="w-4 h-4" />
            Contact support
          </Link>

          <button
            type="button"
            onClick={() => { logout(); navigate('/login', { replace: true }); }}
            data-testid="subscription-required-logout"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionRequired;
